import { z } from 'zod';

/**
 * Operation-plan singleton — the "war-plan" equivalent for the Operation mode.
 *
 * - Identified by an in-game war number, a free-text title, an operation date,
 *   an objective summary and an ordered list of priorities.
 * - Single row (id = 'current') in DB; editable by editors and admins.
 */

export const operationPlanTitleSchema = z.string().trim().max(120);
export const operationPlanObjectiveSchema = z.string().trim().max(2000);
export const operationPlanObjectiveDefenseSchema = z.string().trim().max(2000);
export const operationPlanPrioritySchema = z.string().trim().max(200);
export const operationPlanPrioritiesSchema = z
  .array(operationPlanPrioritySchema)
  .max(9);

export const operationPlanObjectiveTypeSchema = z.enum(['attack', 'defense', 'mixed']);
export const operationPlanWarNumberSchema = z
  .number()
  .int()
  .min(0)
  .max(1_000_000);
/** ISO 8601 date (YYYY-MM-DD) or empty string. */
export const operationPlanDateSchema = z
  .string()
  .trim()
  .max(40)
  .refine(
    (v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v) || !Number.isNaN(Date.parse(v)),
    { message: 'date must be empty or a valid ISO date' },
  );

export const operationPlanSchema = z.object({
  warNumber: operationPlanWarNumberSchema,
  title: operationPlanTitleSchema,
  date: operationPlanDateSchema,
  objective: operationPlanObjectiveSchema,
  objectiveDefense: operationPlanObjectiveDefenseSchema,
  objectiveType: operationPlanObjectiveTypeSchema,
  priorities: operationPlanPrioritiesSchema,
  updatedAt: z.string().datetime(),
  updatedBy: z.string().nullable(),
});
export type OperationPlanDTO = z.infer<typeof operationPlanSchema>;

export const patchOperationPlanSchema = z
  .object({
    warNumber: operationPlanWarNumberSchema.optional(),
    title: operationPlanTitleSchema.optional(),
    date: operationPlanDateSchema.optional(),
    objective: operationPlanObjectiveSchema.optional(),
    objectiveDefense: operationPlanObjectiveDefenseSchema.optional(),
    objectiveType: operationPlanObjectiveTypeSchema.optional(),
    priorities: operationPlanPrioritiesSchema.optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: 'Patch must include at least one field',
  });
export type PatchOperationPlanInput = z.infer<typeof patchOperationPlanSchema>;

export const DEFAULT_OPERATION_PLAN: Omit<OperationPlanDTO, 'updatedAt' | 'updatedBy'> = {
  warNumber: 0,
  title: '',
  date: '',
  objective: '',
  objectiveDefense: '',
  objectiveType: 'attack',
  priorities: [],
};
